import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  XMarkIcon,
  FireIcon,
  HomeIcon,
  PaperAirplaneIcon,
  PlusCircleIcon,
  CheckCircleIcon,
  ChevronRightIcon
} from '@heroicons/react/24/solid';
import {
  ComposedChart,
  Bar,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

/**
 * Get season average for a specific stat from seasonSummary
 * Maps stat keys to the corresponding season average fields
 */
function getSeasonAvgForStat(seasonSummary, statKey, sport) {
  if (!seasonSummary) return null;

  // NBA stat key mapping
  if (sport === 'nba') {
    const mapping = {
      'points': seasonSummary.points_avg,
      'rebounds': seasonSummary.rebounds_avg,
      'assists': seasonSummary.assists_avg,
      'steals': seasonSummary.steals_avg,
      'blocks': seasonSummary.blocks_avg,
      'three_pm': null, // Not available in season summary yet
    };
    return mapping[statKey] ?? null;
  }

  // NHL stat key mapping (totals, not averages - need to calculate per game)
  if (sport === 'nhl') {
    const gamesPlayed = seasonSummary.games_played || 1;
    const mapping = {
      'points': seasonSummary.points ? seasonSummary.points / gamesPlayed : null,
      'goals': seasonSummary.goals ? seasonSummary.goals / gamesPlayed : null,
      'assists': seasonSummary.assists ? seasonSummary.assists / gamesPlayed : null,
      'shots': seasonSummary.shots ? seasonSummary.shots / gamesPlayed : null,
    };
    const val = mapping[statKey];
    return val ? parseFloat(val.toFixed(2)) : null;
  }

  // MLB stat key mapping (season_summary holds totals -> per-game average)
  if (sport === 'mlb') {
    const gamesPlayed = seasonSummary.games_played || 1;
    const totals = {
      'hits': seasonSummary.hits,
      'total_bases': seasonSummary.total_bases,
      'home_runs': seasonSummary.home_runs,
      'rbis': seasonSummary.rbis,
      'runs': seasonSummary.runs,
      'strikeouts': seasonSummary.strikeouts,
      'earned_runs': seasonSummary.earned_runs,
    };
    const total = totals[statKey];
    return (total !== undefined && total !== null) ? parseFloat((total / gamesPlayed).toFixed(2)) : null;
  }

  // NFL and other sports - no season avg mapping yet
  return null;
}

/**
 * Get available stat types for a sport
 */
function getAvailableStats(sport, playerPosition, isPitcher = false) {
  const position = playerPosition?.toUpperCase() || '';

  if (sport === 'nba') {
    return [
      { key: 'points', label: 'Points', shortLabel: 'PTS' },
      { key: 'rebounds', label: 'Rebounds', shortLabel: 'REB' },
      { key: 'assists', label: 'Assists', shortLabel: 'AST' },
      { key: 'three_pm', label: '3-Pointers', shortLabel: '3PM' },
      { key: 'steals', label: 'Steals', shortLabel: 'STL' },
      { key: 'blocks', label: 'Blocks', shortLabel: 'BLK' },
    ];
  }

  if (sport === 'nhl') {
    // For hockey skaters: points, goals, assists, shots
    // Removed hits and blocked_shots as they are less relevant for prop betting
    return [
      { key: 'points', label: 'Points', shortLabel: 'PTS' },
      { key: 'goals', label: 'Goals', shortLabel: 'G' },
      { key: 'assists', label: 'Assists', shortLabel: 'A' },
      { key: 'shots', label: 'Shots', shortLabel: 'SOG' },
    ];
  }

  if (sport === 'nfl') {
    // Position-specific stats
    if (position === 'QB') {
      return [
        { key: 'passing_yards', label: 'Pass Yards', shortLabel: 'YDS' },
        { key: 'passing_touchdowns', label: 'Pass TDs', shortLabel: 'TD' },
        { key: 'completions', label: 'Completions', shortLabel: 'CMP' },
        { key: 'interceptions', label: 'Interceptions', shortLabel: 'INT' },
        { key: 'rushing_yards', label: 'Rush Yards', shortLabel: 'RUSH' },
      ];
    }
    if (position === 'RB') {
      return [
        { key: 'rushing_yards', label: 'Rush Yards', shortLabel: 'YDS' },
        { key: 'rushing_touchdowns', label: 'Rush TDs', shortLabel: 'TD' },
        { key: 'receptions', label: 'Receptions', shortLabel: 'REC' },
        { key: 'receiving_yards', label: 'Rec Yards', shortLabel: 'RYDS' },
        { key: 'carries', label: 'Carries', shortLabel: 'CAR' },
      ];
    }
    if (['WR', 'TE'].includes(position)) {
      return [
        { key: 'receiving_yards', label: 'Rec Yards', shortLabel: 'YDS' },
        { key: 'receptions', label: 'Receptions', shortLabel: 'REC' },
        { key: 'receiving_touchdowns', label: 'Rec TDs', shortLabel: 'TD' },
        { key: 'targets', label: 'Targets', shortLabel: 'TGT' },
      ];
    }
    // Default NFL stats
    return [
      { key: 'rushing_yards', label: 'Rush Yards', shortLabel: 'RUSH' },
      { key: 'receiving_yards', label: 'Rec Yards', shortLabel: 'REC' },
      { key: 'rushing_touchdowns', label: 'Rush TDs', shortLabel: 'RTD' },
      { key: 'receiving_touchdowns', label: 'Rec TDs', shortLabel: 'RECTD' },
    ];
  }

  if (sport === 'mlb') {
    // Keys must match recent_games[].stats from the player-details API
    if (isPitcher || ['P', 'SP', 'RP'].includes(position)) {
      return [
        { key: 'strikeouts', label: 'Strikeouts', shortLabel: 'K' },
        { key: 'quality_start', label: 'Quality Start', shortLabel: 'QS' },
        { key: 'outs', label: 'Outs', shortLabel: 'OUT' },
        { key: 'earned_runs', label: 'Earned Runs', shortLabel: 'ER' },
      ];
    }
    return [
      { key: 'hits', label: 'Hits', shortLabel: 'H' },
      { key: 'total_bases', label: 'Total Bases', shortLabel: 'TB' },
      { key: 'home_runs', label: 'Home Runs', shortLabel: 'HR' },
      { key: 'rbis', label: 'RBIs', shortLabel: 'RBI' },
    ];
  }

  return [{ key: 'points', label: 'Points', shortLabel: 'PTS' }];
}

// Pitching stat types — used to detect a pitcher when the dashboard passes no
// position (it often doesn't), and to map a streak's stat_type to a tab key.
const PITCHING_STAT_TYPES = ['quality_start', 'pitcher_strikeouts', 'strikeouts',
  'pitching_outs', 'outs', 'earned_runs'];

function detectPitcher(player, playerDetails, streakData) {
  const isP = (s) => ['P', 'SP', 'RP'].includes((s || '').toUpperCase());
  return (
    isP(player?.position) ||
    isP(playerDetails?.position) ||
    playerDetails?.is_pitcher === true ||
    PITCHING_STAT_TYPES.includes((streakData?.stat_type || '').toLowerCase()) ||
    PITCHING_STAT_TYPES.includes((playerDetails?.active_streaks?.[0]?.stat_type || '').toLowerCase())
  );
}

// Streak stat_type -> the modal's tab key (e.g. pitcher_strikeouts -> strikeouts).
function streakTabKey(statType) {
  const raw = (statType || '').toLowerCase();
  return ({ pitcher_strikeouts: 'strikeouts', pitching_outs: 'outs' })[raw] || raw;
}

/**
 * Stat tabs component for switching between different stat views
 */
function StatTabs({ stats, selectedStat, onSelectStat, activeStreaks }) {
  // Check if a stat has an active streak with high hit rate
  const getStreakInfo = (statKey) => {
    const streak = activeStreaks?.find(s =>
      s.stat_type?.toLowerCase() === statKey.toLowerCase()
    );
    if (!streak) return null;
    return {
      hitRate: streak.hit_rate || 0,
      isHot: streak.hit_rate >= 0.8,
      isBlazing: streak.hit_rate === 1.0
    };
  };

  return (
    <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-border">
      {stats.map((stat) => {
        const isActive = selectedStat === stat.key;
        const streakInfo = getStreakInfo(stat.key);

        return (
          <button
            key={stat.key}
            onClick={() => onSelectStat(stat.key)}
            className={`
              relative px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all
              ${isActive
                ? streakInfo?.isBlazing
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/30'
                  : streakInfo?.isHot
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg shadow-green-500/30'
                    : 'bg-gradient-to-r from-accent-blue to-blue-600 text-white shadow-lg shadow-blue-500/30'
                : streakInfo?.isBlazing
                  ? 'bg-orange-500/10 text-orange-400 border border-orange-500/30 hover:bg-orange-500/20'
                  : streakInfo?.isHot
                    ? 'bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20'
                    : 'bg-bg-secondary hover:bg-bg-secondary/80 text-text-secondary hover:text-text-primary border border-transparent'
              }
            `}
          >
            {stat.shortLabel}
            {streakInfo?.isBlazing && !isActive && (
              <FireIcon className="inline w-3 h-3 ml-1 text-orange-400" />
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Parse date string without timezone shift
 * "2025-12-28" should always display as Dec 28, regardless of timezone
 */
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  // Parse YYYY-MM-DD as local date (not UTC)
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Custom tooltip for the bar chart
 */
function CustomChartTooltip({ active, payload, average, statLabel }) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const value = data.value;
  const isAboveAvg = value > average;

  // Parse date without timezone shift
  const gameDate = data.gameDate ? parseLocalDate(data.gameDate) : null;

  return (
    <div className="bg-bg-primary border border-border rounded-xl p-3 shadow-2xl text-xs min-w-[150px]">
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
        <span className="text-lg">{data.isHome ? '🏠' : '✈️'}</span>
        <span className="font-semibold text-text-primary">
          {data.isHome ? 'vs' : '@'} {data.opponentName || data.opponent}
        </span>
      </div>

      <div className={`text-2xl font-bold mb-2 ${isAboveAvg ? 'text-emerald-400' : 'text-text-primary'}`}>
        {value} <span className="text-sm text-text-secondary font-normal">{statLabel}</span>
      </div>

      <div className="space-y-1 text-[10px]">
        <div className="flex justify-between">
          <span className="text-text-secondary">vs Average:</span>
          <span className={isAboveAvg ? 'text-emerald-400' : 'text-red-400'}>
            {isAboveAvg ? '+' : ''}{(value - average).toFixed(1)}
          </span>
        </div>
        {gameDate && (
          <div className="flex justify-between text-text-secondary/70">
            <span>Date:</span>
            <span>{gameDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Performance bar chart using Recharts - Clean, data-focused visualization with integrated stats
 * Shows last N games with average line, ML prediction, hit rate, and home/away patterns
 */
function GameHistoryBarChart({ games, statKey = 'points', statLabel = 'PTS', sport = 'nba', mlPrediction = null, threshold = null, thresholdDescription = null, hitRate = null, gamesHit = null, gamesWindow = null, seasonAvg = null }) {
  if (!games || games.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-text-secondary text-sm">
        No game data available
      </div>
    );
  }

  // Reverse games so oldest is on left, newest on right (chronological order)
  const sortedGames = [...games].reverse();

  // Extract values and build chart data
  const chartData = sortedGames.map((g, index) => {
    const statsObj = g.stats || g.game_stats || {};
    const val = statsObj[statKey]
      ?? statsObj[statKey.toLowerCase()]
      ?? statsObj[statKey.replace(/_/g, '')]
      ?? g[statKey]
      ?? g[statKey.toLowerCase()]
      ?? (statKey === 'points' ? g.primary_stat_value : null)
      ?? g.stat_value
      ?? g.value
      ?? 0;
    const value = typeof val === 'number' ? val : parseFloat(val) || 0;

    // Get opponent abbreviation
    const oppAbbr = g.opponent_abbreviation || g.opponent_abbr || g.opponent_name?.substring(0, 3)?.toUpperCase() || '';

    // Format date for display (MM/DD) - parse without timezone shift
    let dateLabel = '';
    if (g.game_date) {
      const parts = g.game_date.split('-');
      dateLabel = `${parseInt(parts[1])}/${parseInt(parts[2])}`;
    }

    return {
      // Use opponent + date as unique label to distinguish multiple games vs same team
      opponent: dateLabel ? `${oppAbbr}\n${dateLabel}` : oppAbbr,
      opponentAbbr: oppAbbr,
      opponentName: g.opponent_name,
      value,
      isHome: g.is_home,
      gameDate: g.game_date,
      isRecent: index === sortedGames.length - 1,
    };
  });

  const values = chartData.map(d => d.value);

  // Calculate statistics
  const validValues = values.filter(v => v > 0);
  const recentAvg = validValues.length > 0
    ? validValues.reduce((a, b) => a + b, 0) / validValues.length
    : 0;
  const last3Values = values.slice(-3).filter(v => v > 0);
  const last3Avg = last3Values.length > 0 ? last3Values.reduce((a, b) => a + b, 0) / last3Values.length : 0;

  // Use season average if provided, otherwise use recent games average
  const average = seasonAvg ?? recentAvg;

  // Calculate trend (compare last 3 to season/recent average)
  const trend = last3Avg && average ? ((last3Avg - average) / average * 100) : 0;

  // Get prediction value if available
  const predictionValue = mlPrediction?.predicted_value || null;

  // Calculate home/away splits
  const homeValues = chartData.filter(d => d.isHome && d.value > 0).map(d => d.value);
  const awayValues = chartData.filter(d => !d.isHome && d.value > 0).map(d => d.value);
  const homeAvg = homeValues.length > 0
    ? homeValues.reduce((a, b) => a + b, 0) / homeValues.length
    : 0;
  const awayAvg = awayValues.length > 0
    ? awayValues.reduce((a, b) => a + b, 0) / awayValues.length
    : 0;

  // Add isAboveAvg to chart data for coloring
  const chartDataWithAvg = chartData.map(d => ({
    ...d,
    isAboveAvg: d.value > average,
  }));

  return (
    <div className="space-y-3">
      {/* Stat Cards Row - Compact and colorful */}
      <div className="grid grid-cols-4 gap-2">
        {/* Season Average */}
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-3 border border-blue-500/20">
          <div className="text-[10px] text-blue-300 uppercase tracking-wide font-medium">{seasonAvg ? 'Season' : `L${games.length}`}</div>
          <div className="text-xl font-bold text-blue-400">{average.toFixed(1)}</div>
        </div>

        {/* Last 3 with trend */}
        <div className={`bg-gradient-to-br ${trend > 5 ? 'from-green-500/20 to-green-600/10 border-green-500/20' : trend < -5 ? 'from-red-500/20 to-red-600/10 border-red-500/20' : 'from-gray-500/20 to-gray-600/10 border-gray-500/20'} rounded-xl p-3 border`}>
          <div className={`text-[10px] uppercase tracking-wide font-medium ${trend > 5 ? 'text-green-300' : trend < -5 ? 'text-red-300' : 'text-gray-300'}`}>Last 3</div>
          <div className="flex items-center gap-1">
            <span className={`text-xl font-bold ${trend > 5 ? 'text-green-400' : trend < -5 ? 'text-red-400' : 'text-gray-400'}`}>
              {last3Avg.toFixed(1)}
            </span>
            {trend !== 0 && (
              <span className={`text-xs ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {trend > 0 ? '↑' : '↓'}
              </span>
            )}
          </div>
        </div>

        {/* Hit Rate */}
        <div className={`bg-gradient-to-br ${hitRate >= 0.8 ? 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/20' : hitRate >= 0.6 ? 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/20' : 'from-gray-500/20 to-gray-600/10 border-gray-500/20'} rounded-xl p-3 border`}>
          <div className={`text-[10px] uppercase tracking-wide font-medium ${hitRate >= 0.8 ? 'text-emerald-300' : hitRate >= 0.6 ? 'text-yellow-300' : 'text-gray-300'}`}>Hit Rate</div>
          <div className={`text-xl font-bold ${hitRate >= 0.8 ? 'text-emerald-400' : hitRate >= 0.6 ? 'text-yellow-400' : 'text-gray-400'}`}>
            {hitRate ? `${(hitRate * 100).toFixed(0)}%` : '—'}
          </div>
          {gamesHit !== undefined && gamesWindow && (
            <div className="text-[9px] text-text-secondary">{gamesHit}/{gamesWindow}</div>
          )}
          {thresholdDescription && (
            <div className="text-[9px] text-text-secondary mt-0.5 truncate" title={thresholdDescription}>
              {thresholdDescription}
            </div>
          )}
        </div>

        {/* Home/Away Split */}
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-3 border border-purple-500/20">
          <div className="text-[10px] text-purple-300 uppercase tracking-wide font-medium">Splits</div>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-cyan-400 font-bold">{homeAvg.toFixed(1)}</span>
            <span className="text-text-secondary">/</span>
            <span className="text-pink-400 font-bold">{awayAvg.toFixed(1)}</span>
          </div>
          <div className="text-[9px] text-text-secondary">H / A</div>
        </div>
      </div>

      {/* ML Prediction Banner (if available) */}
      {predictionValue && (
        <div className="flex items-center justify-between bg-gradient-to-r from-orange-500/20 via-amber-500/10 to-transparent rounded-lg px-3 py-2 border border-orange-500/30">
          <div className="flex items-center gap-2">
            <span className="text-lg">🤖</span>
            <span className="text-xs text-orange-300 font-medium">Knewton Prediction</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-orange-400">{predictionValue.toFixed(1)}</span>
            {mlPrediction?.over_probability && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${mlPrediction.over_probability > 0.5 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {mlPrediction.over_probability > 0.5 ? 'OVER' : 'UNDER'} {(Math.max(mlPrediction.over_probability, mlPrediction.under_probability || 0) * 100).toFixed(0)}%
              </span>
            )}
          </div>
        </div>
      )}

      {/* Chart using Recharts - bars and lines share the same Y-axis scale automatically */}
      <div className="bg-bg-primary/50 rounded-xl p-3">
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={chartDataWithAvg} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
            <XAxis
              dataKey="opponent"
              tick={{ fontSize: 9, fill: '#9ca3af' }}
              axisLine={{ stroke: '#374151' }}
              tickLine={false}
            />
            <YAxis
              hide
              domain={[0, 'auto']}
            />
            <Tooltip
              content={<CustomChartTooltip average={average} statLabel={statLabel} />}
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            />

            {/* Average reference line - uses same Y scale as bars */}
            {average > 0 && (
              <ReferenceLine
                y={average}
                stroke="#60a5fa"
                strokeDasharray="5 5"
                strokeWidth={2}
                label={{
                  value: average.toFixed(1),
                  position: 'right',
                  fill: '#60a5fa',
                  fontSize: 10,
                  fontWeight: 'bold',
                }}
              />
            )}

            {/* ML Prediction reference line - uses same Y scale as bars */}
            {predictionValue && (
              <ReferenceLine
                y={predictionValue}
                stroke="#f97316"
                strokeWidth={2}
                label={{
                  value: `🤖 ${predictionValue.toFixed(1)}`,
                  position: 'right',
                  fill: '#f97316',
                  fontSize: 10,
                  fontWeight: 'bold',
                }}
              />
            )}

            {/* Bars with dynamic coloring */}
            <Bar
              dataKey="value"
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            >
              {chartDataWithAvg.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.isAboveAvg ? '#10b981' : '#6b7280'}
                  fillOpacity={entry.isHome ? 1 : 0.7}
                />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>

        {/* Home indicator row below chart */}
        <div className="flex justify-around px-2 mt-1">
          {chartDataWithAvg.map((entry, index) => (
            <div key={index} className="flex-1 text-center">
              {entry.isHome && <span className="text-[8px] text-cyan-400">●</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Legend - colorful and clear */}
      <div className="flex items-center justify-center gap-6 text-[10px] text-text-secondary">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-emerald-500" />
          <span>Above Avg</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-gray-500" />
          <span>Below Avg</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-cyan-400 text-xs">●</span>
          <span>Home</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-0.5 border-t-2 border-dashed border-blue-400/60" />
          <span>Season Avg</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Home/Away split display
 */
function SplitCard({ type, games, stats, icon: Icon }) {
  const isHome = type === 'home';
  return (
    <div className={`
      flex-1 p-4 rounded-xl border
      ${isHome ? 'border-accent-blue/30 bg-accent-blue/5' : 'border-purple-500/30 bg-purple-500/5'}
    `}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${isHome ? 'text-accent-blue' : 'text-purple-400'}`} />
        <span className={`text-sm font-semibold ${isHome ? 'text-accent-blue' : 'text-purple-400'}`}>
          {isHome ? 'Home' : 'Away'}
        </span>
        <span className="text-xs text-text-secondary">({games} games)</span>
      </div>
      <div className="space-y-1">
        {Object.entries(stats).map(([key, value]) => (
          <div key={key} className="flex justify-between text-sm">
            <span className="text-text-secondary capitalize">{key.replace('_avg', '')}</span>
            <span className="font-bold text-text-primary">{typeof value === 'number' ? value.toFixed(1) : value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


/**
 * Loading skeleton for modal content
 */
function LoadingSkeleton() {
  return (
    <div className="p-5 space-y-6 animate-pulse">
      {/* Streaks skeleton */}
      <div>
        <div className="h-4 w-32 bg-bg-secondary rounded mb-3" />
        <div className="grid grid-cols-2 gap-3">
          {[1, 2].map(i => (
            <div key={i} className="h-24 bg-bg-secondary rounded-xl" />
          ))}
        </div>
      </div>
      {/* Chart skeleton */}
      <div className="glass-card p-4">
        <div className="h-4 w-48 bg-bg-secondary rounded mb-4" />
        <div className="h-32 bg-bg-secondary rounded" />
      </div>
      {/* Splits skeleton */}
      <div className="flex gap-3">
        <div className="flex-1 h-32 bg-bg-secondary rounded-xl" />
        <div className="flex-1 h-32 bg-bg-secondary rounded-xl" />
      </div>
    </div>
  );
}

/**
 * Error state display
 */
function ErrorState({ onRetry }) {
  return (
    <div className="p-8 text-center">
      <div className="text-4xl mb-4">⚠️</div>
      <h3 className="text-lg font-semibold text-text-primary mb-2">Failed to load player details</h3>
      <p className="text-sm text-text-secondary mb-4">
        There was an error loading the player data. Please try again.
      </p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-accent-blue hover:bg-accent-blue/90 text-white rounded-lg text-sm font-medium transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * PlayerDetailModal - Redesigned for parlay building
 */
export default function PlayerDetailModal({
  isOpen,
  onClose,
  player,
  playerDetails = null,  // From API
  isLoading = false,     // Loading state
  hasError = false,      // Error state
  onRetry = null,        // Retry callback
  sport = 'nba',
  streakData = null,
  onAddToParlay = null,
  isInParlay = false,
}) {
  const [selectedStreak, setSelectedStreak] = useState(null);
  const [addedToParlay, setAddedToParlay] = useState(isInParlay);
  const [selectedStatTab, setSelectedStatTab] = useState(null);

  // Pitchers get pitching stats even when the dashboard passes no position.
  const isPitcher = detectPitcher(player, playerDetails, streakData);
  const availableStats = getAvailableStats(sport, player?.position, isPitcher);

  // Initialize selected stat tab — default to the streak's stat (mapped to a real
  // tab key), falling back to the first available tab.
  useEffect(() => {
    if (!selectedStatTab && availableStats.length > 0) {
      const want = streakTabKey(streakData?.stat_type
        || playerDetails?.active_streaks?.[0]?.stat_type);
      const match = availableStats.find((s) => s.key === want);
      setSelectedStatTab(match ? match.key : availableStats[0].key);
    }
  }, [streakData, playerDetails, availableStats, selectedStatTab]);

  // Update selectedStreak when streakData changes
  useEffect(() => {
    if (streakData) {
      setSelectedStreak(streakData);
    } else if (playerDetails?.active_streaks?.length > 0) {
      setSelectedStreak(playerDetails.active_streaks[0]);
    }
  }, [streakData, playerDetails]);

  useEffect(() => {
    setAddedToParlay(isInParlay);
  }, [isInParlay]);

  if (!isOpen || !player) return null;

  // Use real data from playerDetails or fall back to mock
  const recentGames = playerDetails?.recent_games || [];
  // Deduplicate streaks by stat_type (keep the one with highest hit_rate)
  const rawStreaks = playerDetails?.active_streaks || [];
  const activeStreaks = Object.values(
    rawStreaks.reduce((acc, streak) => {
      const key = streak.stat_type?.toLowerCase() || streak.id || Math.random();
      if (!acc[key] || (streak.hit_rate || 0) > (acc[key].hit_rate || 0)) {
        acc[key] = streak;
      }
      return acc;
    }, {})
  );
  const splits = playerDetails?.splits || [];
  const seasonSummary = playerDetails?.season_summary || {};
  const mlPredictions = playerDetails?.ml_predictions || [];

  // Get splits data
  const homeSplit = splits.find(s => s.split_type === 'home');
  const awaySplit = splits.find(s => s.split_type === 'away');

  // Determine streak from selected streak that matches current tab
  const currentTabStreak = activeStreaks.find(s =>
    s.stat_type?.toLowerCase() === selectedStatTab
  );

  // Use the streak for the current tab if available, otherwise fall back to selected streak
  const effectiveStreak = currentTabStreak || selectedStreak;

  // IMPORTANT: Hit rate MUST recalculate based on currently selected stat tab
  // First try to get from the streak matching the current tab
  const hitRate = currentTabStreak?.hit_rate
    ?? effectiveStreak?.hit_rate
    ?? (selectedStatTab === streakData?.stat_type?.toLowerCase() ? streakData?.hit_rate : null)
    ?? null;

  const gamesHit = currentTabStreak?.games_hit
    ?? effectiveStreak?.games_hit
    ?? (selectedStatTab === streakData?.stat_type?.toLowerCase() ? streakData?.games_hit : null)
    ?? null;

  const gamesWindow = currentTabStreak?.games_window
    ?? effectiveStreak?.games_window
    ?? (selectedStatTab === streakData?.stat_type?.toLowerCase() ? streakData?.games_window : null)
    ?? 10;

  // Use the selected stat tab for the chart, fallback to streak stat type
  const statKey = selectedStatTab || effectiveStreak?.stat_type?.toLowerCase() || 'points';

  // Get current stat info for display
  const currentStatInfo = availableStats.find(s => s.key === statKey) || { label: 'Points', shortLabel: 'PTS' };

  // Find ML prediction for the currently selected stat
  const currentMlPrediction = mlPredictions.find(p =>
    p.stat_type?.toLowerCase() === statKey.toLowerCase()
  ) || null;

  const handleAddToParlay = () => {
    if (onAddToParlay && selectedStreak) {
      onAddToParlay({
        player,
        streak: selectedStreak,
        sport,
      });
      setAddedToParlay(true);
    }
  };

  const sportColor = sport === 'nba' ? 'orange' : sport === 'nhl' ? 'blue' : 'green';

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden bg-bg-card rounded-2xl border border-border shadow-2xl">
        {/* Header - Compact and clean */}
        <div className={`relative px-5 py-3 bg-gradient-to-r from-${sportColor}-500/10 to-transparent border-b border-border`}>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-bg-secondary transition-colors z-10"
          >
            <XMarkIcon className="w-5 h-5 text-text-secondary" />
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-text-primary">{player.player_name}</h2>
                <span className={`px-2 py-0.5 rounded text-xs font-bold bg-${sportColor}-500/20 text-${sportColor}-400 border border-${sportColor}-500/30`}>
                  {player.team_abbreviation}
                </span>
                {player.position && (
                  <span className="text-xs text-text-secondary">
                    {player.position}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Loading State */}
          {isLoading && <LoadingSkeleton />}

          {/* Error State */}
          {hasError && !isLoading && <ErrorState onRetry={onRetry} />}

          {/* Content - only show when not loading and no error */}
          {!isLoading && !hasError && (
            <div className="p-5 space-y-6">

          {/* Game History Bar Chart with Stat Tabs */}
          {recentGames.length > 0 && (
            <div className="glass-card p-4">
              {/* Stat Tabs - Colorful indicators for hot streaks */}
              <div className="mb-4">
                <StatTabs
                  stats={availableStats}
                  selectedStat={selectedStatTab}
                  onSelectStat={setSelectedStatTab}
                  activeStreaks={activeStreaks}
                />
              </div>

              {/* Game History Chart with integrated stats */}
              <GameHistoryBarChart
                key={`${statKey}-${hitRate}-${gamesHit}`}
                games={recentGames}
                statKey={statKey}
                statLabel={currentStatInfo.shortLabel}
                sport={sport}
                mlPrediction={currentMlPrediction}
                hitRate={hitRate}
                gamesHit={gamesHit}
                gamesWindow={gamesWindow}
                thresholdDescription={currentTabStreak?.threshold_description || effectiveStreak?.threshold_description}
                seasonAvg={getSeasonAvgForStat(seasonSummary, statKey, sport)}
              />
            </div>
          )}

          {/* Home/Away Splits */}
          {(homeSplit || awaySplit) && (
            <div>
              <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                <HomeIcon className="w-4 h-4 text-accent-blue" />
                Home / Away Splits
              </h3>
              <div className="flex gap-3">
                {homeSplit && (
                  <SplitCard
                    type="home"
                    games={homeSplit.games_played}
                    stats={homeSplit.stats}
                    icon={HomeIcon}
                  />
                )}
                {awaySplit && (
                  <SplitCard
                    type="away"
                    games={awaySplit.games_played}
                    stats={awaySplit.stats}
                    icon={PaperAirplaneIcon}
                  />
                )}
              </div>
            </div>
          )}

          {/* Season Summary Stats */}
          {Object.keys(seasonSummary).length > 0 && (
            <div className="glass-card p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">Season Averages</h3>
              <div className="grid grid-cols-4 gap-4">
                {sport === 'nba' && (
                  <>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">{seasonSummary.points_avg || 0}</p>
                      <p className="text-xs text-text-secondary">PPG</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">{seasonSummary.rebounds_avg || 0}</p>
                      <p className="text-xs text-text-secondary">RPG</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">{seasonSummary.assists_avg || 0}</p>
                      <p className="text-xs text-text-secondary">APG</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">
                        {(() => {
                          const fg = seasonSummary.fg_pct;
                          if (!fg) return '0';
                          // Handle different formats: 0.4669, 46.69, or 4669
                          if (fg > 100) return (fg / 100).toFixed(1); // 4669 -> 46.7
                          if (fg > 1) return fg.toFixed(1); // 46.69 -> 46.7
                          return (fg * 100).toFixed(1); // 0.4669 -> 46.7
                        })()}%
                      </p>
                      <p className="text-xs text-text-secondary">FG%</p>
                    </div>
                  </>
                )}
                {sport === 'nhl' && (
                  <>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">{seasonSummary.points || 0}</p>
                      <p className="text-xs text-text-secondary">Points</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">{seasonSummary.goals || 0}</p>
                      <p className="text-xs text-text-secondary">Goals</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">{seasonSummary.assists || 0}</p>
                      <p className="text-xs text-text-secondary">Assists</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">{seasonSummary.shots || 0}</p>
                      <p className="text-xs text-text-secondary">SOG</p>
                    </div>
                  </>
                )}
                {sport === 'nfl' && (
                  <>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">{seasonSummary.passing_yards || seasonSummary.rushing_yards || seasonSummary.receiving_yards || 0}</p>
                      <p className="text-xs text-text-secondary">Yards</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">{seasonSummary.passing_tds || seasonSummary.rushing_tds || seasonSummary.receiving_tds || 0}</p>
                      <p className="text-xs text-text-secondary">TDs</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">{seasonSummary.games_played || 0}</p>
                      <p className="text-xs text-text-secondary">Games</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-text-primary">
                        {seasonSummary.rush_yards_avg || seasonSummary.rec_yards_avg || 0}
                      </p>
                      <p className="text-xs text-text-secondary">Yds/G</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* AI Insight - if we have prediction data */}
          {selectedStreak?.has_prediction && (
            <div className="p-4 rounded-xl border border-accent-blue/30 bg-gradient-to-r from-accent-blue/10 to-purple-500/10">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🤖</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-text-primary">AI Prediction</h3>
                    <span className={`
                      px-2 py-0.5 rounded text-xs font-bold
                      ${selectedStreak.recommendation === 'STRONG' ? 'bg-success/20 text-success' : 'bg-warning/20 text-warning'}
                    `}>
                      {selectedStreak.recommendation || 'LEAN'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-xs text-text-secondary">Predicted</p>
                      <p className="text-lg font-bold text-accent-blue">{selectedStreak.prediction_value}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-secondary">Confidence</p>
                      <p className="text-lg font-bold text-text-primary">{Math.round((selectedStreak.prediction_confidence || 0) * 100)}%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
            </div>
          )}
        </div>

        {/* Footer with Add to Parlay button */}
        <div className="p-4 border-t border-border bg-bg-secondary/50">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              {selectedStreak && (
                <p className="text-sm text-text-secondary">
                  <span className="font-medium text-text-primary">{player.player_name}</span>
                  {' '}{selectedStreak.threshold_description}
                </p>
              )}
            </div>

            <button
              onClick={handleAddToParlay}
              disabled={!selectedStreak || !onAddToParlay}
              className={`
                flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all
                ${addedToParlay
                  ? 'bg-success/20 text-success border-2 border-success'
                  : 'bg-gradient-to-r from-accent-blue to-purple-500 hover:from-accent-blue/90 hover:to-purple-500/90 text-white shadow-lg hover:shadow-xl hover:scale-[1.02]'
                }
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
              `}
            >
              {addedToParlay ? (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  Added to Parlay
                </>
              ) : (
                <>
                  <PlusCircleIcon className="w-5 h-5" />
                  Add to Parlay
                  <ChevronRightIcon className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
